// Authored Concept Canvas content for the "order"-mode canvas lessons.
//
// Despite living under claudeDevCourse/, this file is keyed by lessonID and serves
// EVERY Claude course — CD01 (CD1##), CD02 (V2###) and CD03 (V3###) all draw from it.
//
// WHY THIS EXISTS
// The canvas (see components/PromptLabHub/ConceptCanvas.js) grades an "order"
// exercise by requiring the learner to reproduce the EXACT sequence the nodes were
// authored in (`correctIds`). When those nodes are left to per-user AI generation,
// nothing guarantees the generated order matches the order the card *descriptions*
// teach — so a perfectly sensible arrangement can never satisfy the check and the
// lesson becomes impossible to complete.
//
// The fix: pin the canonical canvas (mode + node order + copy) here and have the
// hub override `definition.canvas` on load (PromptLabHubComponent). This mirrors
// the authored-starter precedent in ../../starterTemplate/authoredLessons.js and
// wins over whatever the generator/cache produced — no cache purge or version
// bump required.
//
// SCOPE
// Two separate reasons land a canvas in this file.
//
// 1. COMPLETABILITY — "order"-mode lessons are graded on exact sequence, so all of
//    them are pinned: CD102/V3104 (input→logic→output), CD106/V3107 (breaking a
//    problem into steps), V3305 (how HTML, CSS and JavaScript fit together) and
//    V2602 (installing Claude Code).
//
// 2. FACTUAL ACCURACY — CD02's Module 6 describes Claude Code, a real external
//    product whose surfaces, pricing and screen change without notice. A generated
//    canvas would invent UI parts and teach them confidently, so V2601 is pinned
//    even though "explore" mode completes on tap-all regardless of order. Its prose
//    is pinned for the same reason, server-side, in
//    backend/src/data/claudeCodeLessonProse.js — keep the two in step.
//
//    Module 6's other UI lesson, V2604 ("Reading the Claude Code screen"), is NOT
//    here: it is a webLab whose scaffold is a hand-built replica of that screen, in
//    starterTemplate/authoredLessons.js. Cards describe a layout; the replica IS one.
//
// Every other "explore" lesson stays AI-generated; add it here the same way if you
// later want it deterministic.
//
// CD02's Module 1 canvases (V2104, V2108) are deliberately NOT here — they predate
// this file's use beyond CD01 and are left exactly as they shipped. Module 6 is the
// only part of CD02 that enters this file.
//
// Each node needs a STABLE, UNIQUE `id`: uniqueness makes the sequence solvable,
// and a defined id avoids the all-`undefined` shortcut that would auto-complete
// the exercise. `id`s are kept identical across locales for clarity.

// CD01 and CD03 teach these two lessons identically, so they SHARE one authored
// canvas rather than keeping two copies that could drift apart.
const HOW_A_PROGRAM_RUNS = {
        en: {
            caption: "Drag these into the order a program runs.",
            mode: "order",
            nodes: [
                {
                    id: "input",
                    icon: "⌨️",
                    label: "Input",
                    description:
                        "The program receives something to work with — a user types into a form, a file is read, or another program sends a request.",
                },
                {
                    id: "logic",
                    icon: "⚙️",
                    label: "Logic",
                    description:
                        "It processes that data: making decisions, doing calculations, and following the rules you wrote to figure out what to do.",
                },
                {
                    id: "output",
                    icon: "📤",
                    label: "Output",
                    description:
                        "It shows or sends the result — text on the screen, a saved file, or a response back to whoever asked.",
                },
            ],
        },
        es: {
            caption: "Arrastra estos pasos al orden en que se ejecuta un programa.",
            mode: "order",
            nodes: [
                {
                    id: "input",
                    icon: "⌨️",
                    label: "Entrada",
                    description:
                        "El programa recibe algo con lo que trabajar: un usuario escribe en un formulario, se lee un archivo u otro programa envía una solicitud.",
                },
                {
                    id: "logic",
                    icon: "⚙️",
                    label: "Lógica",
                    description:
                        "Procesa esos datos: toma decisiones, hace cálculos y sigue las reglas que escribiste para decidir qué hacer.",
                },
                {
                    id: "output",
                    icon: "📤",
                    label: "Salida",
                    description:
                        "Muestra o envía el resultado: texto en la pantalla, un archivo guardado o una respuesta a quien lo pidió.",
                },
            ],
        },
};

// Copy captured verbatim from the good generation; order pinned.
const BREAKING_A_PROBLEM_INTO_STEPS = {
        en: {
            caption: "Drag these steps into the order you'd tackle them when building a tip calculator app.",
            mode: "order",
            nodes: [
                {
                    id: "understand-goal",
                    icon: "🎯",
                    label: "Understand the goal",
                    description:
                        "What should the app do? A tip calculator takes a bill amount and tip percentage, then shows the tip and total. Write down the core purpose in one sentence.",
                },
                {
                    id: "sketch-interface",
                    icon: "✏️",
                    label: "Sketch the interface",
                    description:
                        "What does the user see? Two input boxes (bill, tip %), a calculate button, and a results area. Draw or describe it before writing any code.",
                },
                {
                    id: "build-visible-parts",
                    icon: "🖼️",
                    label: "Build the visible parts",
                    description:
                        "Create the inputs, button, and results display first. Make sure they appear on screen and look reasonable. No logic yet—just the interface.",
                },
                {
                    id: "add-calculation",
                    icon: "🧮",
                    label: "Add the calculation",
                    description:
                        "Now write (or ask Claude to write) the code that runs when the user clicks Calculate: read the inputs, do the math, display the results.",
                },
                {
                    id: "refine-polish",
                    icon: "✨",
                    label: "Refine and polish",
                    description:
                        "Improve the design, add helpful labels, format numbers as currency. Small tweaks make the app feel professional and easy to use.",
                },
                {
                    id: "test-edge-cases",
                    icon: "🧪",
                    label: "Test edge cases",
                    description:
                        "What if someone enters zero, or leaves a field blank, or types letters? Test these scenarios and handle them gracefully with clear messages.",
                },
            ],
        },
        es: {
            caption: "Arrastra estos pasos al orden en que los abordarías al crear una app de calculadora de propinas.",
            mode: "order",
            nodes: [
                {
                    id: "understand-goal",
                    icon: "🎯",
                    label: "Entender el objetivo",
                    description:
                        "¿Qué debe hacer la app? Una calculadora de propinas toma el monto de la cuenta y el porcentaje de propina, y muestra la propina y el total. Escribe el propósito central en una sola frase.",
                },
                {
                    id: "sketch-interface",
                    icon: "✏️",
                    label: "Bocetar la interfaz",
                    description:
                        "¿Qué ve el usuario? Dos campos de entrada (cuenta, % de propina), un botón de calcular y un área de resultados. Dibújalo o descríbelo antes de escribir código.",
                },
                {
                    id: "build-visible-parts",
                    icon: "🖼️",
                    label: "Construir las partes visibles",
                    description:
                        "Crea primero los campos, el botón y el área de resultados. Asegúrate de que aparezcan en pantalla y se vean bien. Sin lógica todavía—solo la interfaz.",
                },
                {
                    id: "add-calculation",
                    icon: "🧮",
                    label: "Agregar el cálculo",
                    description:
                        "Ahora escribe (o pídele a Claude que escriba) el código que se ejecuta cuando el usuario hace clic en Calcular: lee los datos, haz la operación y muestra los resultados.",
                },
                {
                    id: "refine-polish",
                    icon: "✨",
                    label: "Refinar y pulir",
                    description:
                        "Mejora el diseño, agrega etiquetas útiles y da formato de moneda a los números. Los pequeños ajustes hacen que la app se sienta profesional y fácil de usar.",
                },
                {
                    id: "test-edge-cases",
                    icon: "🧪",
                    label: "Probar casos límite",
                    description:
                        "¿Qué pasa si alguien ingresa cero, deja un campo vacío o escribe letras? Prueba estos escenarios y manéjalos con mensajes claros.",
                },
            ],
        },
};

// V3305 — "How the three fit together — and where a change goes".
//
// The keystone of CD03's Module 3: the four lessons before it meet HTML, CSS and
// JavaScript one at a time, and this one is where they connect. It is deliberately
// the only no-typing lesson in the module — the payload is the mental model, and
// the last two nodes are what makes "ask Claude to change the button" make sense.
const HOW_THE_WEB_PAGE_COMES_TOGETHER = {
    en: {
        caption: "Drag these into the order the browser does them when someone opens your page.",
        mode: "order",
        nodes: [
            {
                id: "read-html",
                icon: "📄",
                label: "Read the HTML",
                description:
                    "The browser reads your file top to bottom and builds the page's pieces — the headings, the paragraphs, the buttons. At this moment everything is there but plain and unstyled.",
            },
            {
                id: "apply-css",
                icon: "🎨",
                label: "Apply the CSS",
                description:
                    "Now it applies your style rules: colours, sizes, spacing. Nothing new appears — the same pieces just start looking the way you asked.",
            },
            {
                id: "run-javascript",
                icon: "⚡",
                label: "Run the JavaScript",
                description:
                    "It runs your code once. Usually that code doesn't change anything yet — it just says what SHOULD happen later, like \"when this button is clicked, do that\".",
            },
            {
                id: "user-acts",
                icon: "👆",
                label: "Someone clicks or types",
                description:
                    "The page sits there waiting. Then a real person taps a button or fills in a box — this is the moment the page has been waiting for.",
            },
            {
                id: "page-updates",
                icon: "🔄",
                label: "JavaScript changes the page",
                description:
                    "The instruction from step 3 finally fires and updates what's on screen. No reload, no new file — the same page just becomes different.",
            },
        ],
    },
    es: {
        caption: "Arrastra estos pasos al orden en que el navegador los hace cuando alguien abre tu página.",
        mode: "order",
        nodes: [
            {
                id: "read-html",
                icon: "📄",
                label: "Leer el HTML",
                description:
                    "El navegador lee tu archivo de arriba abajo y arma las piezas de la página: los títulos, los párrafos, los botones. En este momento todo está ahí, pero simple y sin estilo.",
            },
            {
                id: "apply-css",
                icon: "🎨",
                label: "Aplicar el CSS",
                description:
                    "Ahora aplica tus reglas de estilo: colores, tamaños, espaciado. No aparece nada nuevo — las mismas piezas empiezan a verse como pediste.",
            },
            {
                id: "run-javascript",
                icon: "⚡",
                label: "Ejecutar el JavaScript",
                description:
                    "Ejecuta tu código una vez. Normalmente ese código todavía no cambia nada: solo deja dicho qué DEBERÍA pasar después, como «cuando hagan clic en este botón, haz esto».",
            },
            {
                id: "user-acts",
                icon: "👆",
                label: "Alguien hace clic o escribe",
                description:
                    "La página se queda esperando. Entonces una persona real toca un botón o llena una casilla: este es el momento que la página estaba esperando.",
            },
            {
                id: "page-updates",
                icon: "🔄",
                label: "JavaScript cambia la página",
                description:
                    "La instrucción del paso 3 por fin se activa y actualiza lo que se ve en pantalla. Sin recargar, sin archivo nuevo — la misma página simplemente se vuelve distinta.",
            },
        ],
    },
};

// V2601 — "Meet Claude Code: Claude on your own computer".
//
// The whole course so far has happened inside a chat box that cannot touch a file.
// This lesson's only job is the distinction between that and a tool that opens a
// real folder — plus the cost, stated plainly and early. Anyone who is going to be
// stopped by the paid plan should find out here, in lesson one, and not after
// downloading an installer.
const WHERE_CLAUDE_CODE_LIVES = {
    en: {
        caption: "Tap each one to see where Claude can and can't reach.",
        mode: "explore",
        nodes: [
            {
                id: "chat",
                icon: "💬",
                label: "The chat you already know",
                description:
                    "Everything you have done in this course. You describe what you want, Claude writes code back, and YOU copy it into your page. Claude never sees your files — it only sees what you paste.",
            },
            {
                id: "desktop",
                icon: "🖥️",
                label: "The Claude desktop app",
                description:
                    "An app you install on your computer. It has a Code tab, and that tab IS Claude Code — you open your project folder in it, and from then on Claude can read your real files and change them for you. No copying and pasting, and no terminal.",
            },
            {
                id: "terminal",
                icon: "⌨️",
                label: "Claude Code in the terminal",
                description:
                    "Exactly the same tool, typed instead of clicked. Developers who already live in the terminal prefer it. You do not need it — the app does the same work.",
            },
            {
                id: "editor",
                icon: "🧩",
                label: "Inside a code editor",
                description:
                    "Claude Code also plugs into editors like VS Code, so it sits beside the file you are looking at. Worth knowing about later; skip it for now.",
            },
            {
                id: "cost",
                icon: "🔑",
                label: "What it costs",
                description:
                    "Claude Code needs a paid Claude plan — Pro, Max, Team or Enterprise, or a Console account with credits. The free Claude plan does not include it. Read this module either way: knowing what the tool does is worth it before you decide to pay for it.",
            },
        ],
    },
    es: {
        caption: "Toca cada uno para ver hasta dónde llega Claude en cada caso.",
        mode: "explore",
        nodes: [
            {
                id: "chat",
                icon: "💬",
                label: "El chat que ya conoces",
                description:
                    "Todo lo que has hecho en este curso. Tú describes lo que quieres, Claude te devuelve código y TÚ lo copias a tu página. Claude nunca ve tus archivos: solo ve lo que le pegas.",
            },
            {
                id: "desktop",
                icon: "🖥️",
                label: "La aplicación de escritorio de Claude",
                description:
                    "Una aplicación que instalas en tu computadora. Tiene una pestaña Code, y esa pestaña ES Claude Code: abres en ella la carpeta de tu proyecto y, desde ese momento, Claude puede leer tus archivos de verdad y modificarlos por ti. Sin copiar y pegar, y sin terminal.",
            },
            {
                id: "terminal",
                icon: "⌨️",
                label: "Claude Code en la terminal",
                description:
                    "Exactamente la misma herramienta, escrita en vez de clicada. Quienes ya viven en la terminal la prefieren. Tú no la necesitas: la aplicación hace el mismo trabajo.",
            },
            {
                id: "editor",
                icon: "🧩",
                label: "Dentro de un editor de código",
                description:
                    "Claude Code también se integra en editores como VS Code, así que queda junto al archivo que estás viendo. Está bien saberlo para más adelante; por ahora sáltatelo.",
            },
            {
                id: "cost",
                icon: "🔑",
                label: "Cuánto cuesta",
                description:
                    "Claude Code requiere un plan de pago de Claude: Pro, Max, Team o Enterprise, o una cuenta de Console con créditos. El plan gratuito de Claude no lo incluye. Lee este módulo de todos modos: saber qué hace la herramienta vale la pena antes de decidir pagarla.",
            },
        ],
    },
};

export const AUTHORED_CANVASES = {
    // CD01 — "Claude AI Developer"
    CD102: HOW_A_PROGRAM_RUNS,
    CD106: BREAKING_A_PROBLEM_INTO_STEPS,

    // CD03 — "Claude AI Developer V3". Same two Module 1 lessons as CD01 under new
    // ids, plus Module 3's keystone.
    V3104: HOW_A_PROGRAM_RUNS,
    V3107: BREAKING_A_PROBLEM_INTO_STEPS,
    V3305: HOW_THE_WEB_PAGE_COMES_TOGETHER,

    // CD02 Module 6 — Claude Code. Pinned for accuracy, not just completability;
    // see SCOPE at the top of this file.
    //
    // V2602 is NOT here. It was an "order" canvas — drag the five install steps into
    // sequence — until it became a `checklist` lesson: the answer was printed, in
    // order and numbered, in the lesson pane beside it, and sequence recall is not
    // what makes an install fail. Its steps now live in authoredChecklists.js, where
    // they can branch on the operating system and survive the student leaving to run
    // the installer.
    V2601: WHERE_CLAUDE_CODE_LIVES,

    // CD04 — "Claude AI Developer V4". Assembled from the earlier courses, so its
    // three canvas lessons point at the same canvases under new ids. Aliased rather
    // than copied: an "order" canvas grades on exact node sequence, so a second copy
    // that drifted by one node would be unsolvable rather than merely different.
    // V4104/V4109 are Module 1; V4304 is the keystone of Module 3.
    //
    // WHERE_CLAUDE_CODE_LIVES has no CD04 entry. That course opens its Claude Code
    // module on the build spec instead of on "Meet Claude Code" — the lesson this
    // canvas belongs to is not in CD04 at all.
    V4104: HOW_A_PROGRAM_RUNS,
    V4109: BREAKING_A_PROBLEM_INTO_STEPS,
    V4304: HOW_THE_WEB_PAGE_COMES_TOGETHER,
};

/**
 * Resolve the authored canvas for a lesson in the requested locale.
 * Falls back to English, then null when nothing is authored (the caller then
 * keeps the generated canvas).
 *
 * @param {string} lessonID  e.g. "CD106"
 * @param {string} locale    bare locale, e.g. "en" | "es"
 * @returns {{caption:string, mode:string, nodes:Array}|null}
 */
export function buildAuthoredCanvas(lessonID, locale) {
    const entry = AUTHORED_CANVASES[lessonID];
    if (!entry) return null;
    return entry[locale] || entry.en || null;
}
